import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import ProfileHeader from "./ProfileHeader";
import TabNavigation from "./TabNavigation";
import OverviewView from "./views/OverviewView";
import FinalRemarksView from "./views/FinalRemarksView";
import ScreeningQuestionsView from "./views/ScreeningQuestionsView";
import ProctoringLogsView from "./views/ProctoringLogsView";
import ScreenshotsView from "./views/ScreenshotsView";
import PsychometricAnalysisView from "../cefrFeedback/views/PsychometricAnalysisView";
import { useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getInterview } from "../../../redux/action";
import { AppDispatch } from "../../../redux/store";
import {
  getFeedbackForm,
  getLoading,
  getProfileForm,
  getShowFinalRemarks,
} from "../../../redux/reducer/incbotFeedbackReducer";
import { AnimatePresence, motion } from "framer-motion";
import skeletonLoad from "../../../assets/gifs/skeleton-load.gif";
import { Loader } from "../../ui/loader";
import { capitalizeAll, generateUrl } from "../../../utils/helper";
import { RatingSystem } from "./views/RatingSystemView";
import NotFound from "../notFound/NotFound";

interface IndexProps {
  skipFetch?: boolean;
}

const Index = ({ skipFetch = false }: IndexProps) => {
  const { roomId = "", interviewId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const signature = searchParams.get("signature") || "";
  const expires = searchParams.get("expires") || "";
  const dispatch = useDispatch<AppDispatch>();
  const profileForm = useSelector(getProfileForm);
  const feedbackForm = useSelector(getFeedbackForm);
  const showFinalRemarks = useSelector(getShowFinalRemarks);
  const loading = useSelector(getLoading);

  const [activeTab, setActiveTab] = useState("overview");
  const prevTabIndex = useRef(0);
  const isPsychometricAssessment = profileForm?.interviewType === "psychometric_assessment";

  const showPsychometricAnalysis =
    isPsychometricAssessment && Boolean(feedbackForm?.psychometricSkillsScores?.length);

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "final-remarks", label: "Final Remarks" },
    ...(showPsychometricAnalysis
      ? [
          {
            id: "psychometric-analysis",
            label: "Psychometric Analysis" as const,
          },
        ]
      : []),
    { id: "screening-questions", label: "Screening Questions" },
  ];

  if (feedbackForm?.proctoringLogs?.status)
    tabs.push({ id: "proctoring-logs", label: "Proctoring Logs" });
  if (feedbackForm?.screenshots?.status) tabs.push({ id: "screenshots", label: "Screenshots" });
  tabs.push({ id: "rating-system", label: "Rating System" });

  const currentTabIndex = tabs.findIndex((tab) => tab.id === activeTab);
  const direction = currentTabIndex > prevTabIndex.current ? 1 : -1;

  useEffect(() => {
    if (!skipFetch) {
      dispatch(getInterview({ roomId, interviewId, signature, expires }));
    }
  }, [skipFetch]);

  useEffect(() => {
    if (
      feedbackForm &&
      "feedbackTemplate" in feedbackForm &&
      feedbackForm?.feedbackTemplate === false
    ) {
      const currentUrl = new URL(window.location.href);
      const pathname = currentUrl.pathname.replace("/incbot", "");
      const newUrl = new URL(
        `${generateUrl("meeting")}/feedback/room${pathname}${currentUrl.search}`
      );
      window.location.href = newUrl.toString();
    }
  }, [feedbackForm?.feedbackTemplate]);

  const renderActiveView = () => {
    switch (activeTab) {
      case "overview":
        return <OverviewView key="overview" />;
      case "final-remarks":
        return <FinalRemarksView key="final-remarks" />;
      case "psychometric-analysis":
        return <PsychometricAnalysisView key="psychometric-analysis" />;
      case "screening-questions":
        return <ScreeningQuestionsView key="screening-questions" />;
      case "proctoring-logs":
        return <ProctoringLogsView key="proctoring-logs" />;
      case "screenshots":
        return <ScreenshotsView key="screenshots" />;
      case "rating-system":
        return <RatingSystem key="rating-system" />;
      default:
        return <OverviewView key="overview" />;
    }
  };

  const handleTabChange = (id: string) => {
    prevTabIndex.current = tabs.findIndex((tab) => tab.id === activeTab);
    setActiveTab(id);
  };

  useEffect(() => {
    if (profileForm?.name) {
      document.title = `${capitalizeAll(profileForm?.name)} Feedback Report`;
    }
  }, [profileForm]);

  return loading || !feedbackForm ? (
    <Loader gifSrc={skeletonLoad} size="xxl" fullscreen />
  ) : feedbackForm?.version > 10 || feedbackForm?.feedbackTemplate ? (
    profileForm && feedbackForm ? (
      <div className="min-h-screen min-w-[320px] flex w-full justify-center">
        {profileForm?.name && (
          <Helmet>
            <title>{capitalizeAll(profileForm.name)} Feedback Report | InCruiter</title>
            <meta
              name="description"
              content={`Interview feedback report for ${profileForm.name}${profileForm.position ? ` – ${profileForm.position}` : ""}${profileForm.company ? ` at ${profileForm.company}` : ""}`}
            />
            <meta
              property="og:title"
              content={`${capitalizeAll(profileForm.name)} Feedback Report | InCruiter`}
            />
            <meta
              property="og:description"
              content={`Interview feedback report for ${profileForm.name}${profileForm.position ? ` – ${profileForm.position}` : ""}${profileForm.company ? ` at ${profileForm.company}` : ""}`}
            />
            <meta
              name="twitter:title"
              content={`${capitalizeAll(profileForm.name)} Feedback Report | InCruiter`}
            />
            <meta
              name="twitter:description"
              content={`Interview feedback report for ${profileForm.name}${profileForm.position ? ` – ${profileForm.position}` : ""}${profileForm.company ? ` at ${profileForm.company}` : ""}`}
            />
          </Helmet>
        )}
        <div className="max-w-6xl min-w-[320px] w-full bg-gray-50 print-a3-container">
          <ProfileHeader />
          <div className="hidden md:block print:hidden">
            <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />
            <div className="bg-gray-50 overflow-hidden relative">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={activeTab}
                  custom={direction}
                  initial={{ x: direction * 100, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: direction * -100, opacity: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                  {renderActiveView()}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          <div id="print-section" className="md:hidden print:block space-y-8 print:md:block">
            <div className="bg-gray-50">
              <OverviewView key="overview" />
              <div className=""></div>
              {showFinalRemarks && (
                <>
                  <FinalRemarksView key="final-remarks" />
                  <div className="page-break"></div>
                </>
              )}

              {showPsychometricAnalysis && (
                <>
                  <PsychometricAnalysisView key="psychometric-analysis" />
                  <div className="page-break"></div>
                </>
              )}

              <ScreeningQuestionsView key="screening-questions" />
              <div className="page-break"></div>

              {feedbackForm?.proctoringLogs?.status && (
                <>
                  <ProctoringLogsView key="proctoring-logs" />
                  <div className="page-break"></div>
                </>
              )}

              {feedbackForm?.screenshots?.status && (
                <>
                  <ScreenshotsView key="screenshots" />
                  <div className="page-break"></div>
                </>
              )}
              <RatingSystem />
            </div>
          </div>
        </div>
      </div>
    ) : (
      <NotFound expired={true} />
    )
  ) : (
    <NotFound />
  );
};

export default Index;
